import React, { useState, useEffect } from 'react';
import { useUser } from './UserContext';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || '';

const HelpPage = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [faqs, setFaqs] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openFaq, setOpenFaq] = useState(null);
  const [activeTab, setActiveTab] = useState('faq');
  const [playingVideo, setPlayingVideo] = useState(null);

  useEffect(() => {
    fetchFaqs();
    if (user) fetchVideos();
    else setLoading(false);
  }, [user]);

  const fetchFaqs = async () => {
    try {
      const res = await fetch(`${API}/api/help/faqs`);
      if (res.ok) setFaqs(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchVideos = async () => {
    try {
      const token = user?.token || JSON.parse(localStorage.getItem('user') || '{}').token;
      const res = await fetch(`${API}/api/help/videos`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setVideos(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const categories = [...new Set(videos.map(v => v.category || 'genel'))];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-white border-b border-blue-100 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-blue-50 rounded-lg transition">
              <svg className="w-5 h-5 text-blue-700" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>
            </button>
            <div>
              <h1 className="text-xl font-bold text-blue-900">Yardım Merkezi</h1>
              <p className="text-xs text-blue-500">Sık sorulan sorular ve kullanım kılavuzları</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
            </svg>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 bg-white rounded-xl p-1.5 shadow-sm border border-blue-100">
          <button
            onClick={() => setActiveTab('faq')}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-semibold transition ${
              activeTab === 'faq' ? 'bg-blue-600 text-white shadow-md' : 'text-blue-700 hover:bg-blue-50'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
            </svg>
            Sık Sorulan Sorular
          </button>
          <button
            onClick={() => setActiveTab('videos')}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-semibold transition ${
              activeTab === 'videos' ? 'bg-blue-600 text-white shadow-md' : 'text-blue-700 hover:bg-blue-50'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
            </svg>
            Kullanım Videoları
            {!user && (
              <span className="ml-1 px-1.5 py-0.5 bg-orange-100 text-orange-700 text-[10px] rounded-full font-bold">Üye</span>
            )}
          </button>
        </div>

        {/* FAQ Tab */}
        {activeTab === 'faq' && (
          <div className="space-y-3">
            {faqs.length === 0 && !loading && (
              <div className="text-center py-16">
                <svg className="w-16 h-16 text-blue-200 mx-auto mb-4" fill="none" stroke="currentColor" strokeWidth="1" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
                </svg>
                <p className="text-blue-900 font-semibold text-lg">Henüz SSS eklenmemiş</p>
                <p className="text-blue-500 text-sm mt-1">Yakında sık sorulan sorular burada yer alacak.</p>
              </div>
            )}
            {faqs.map((faq, idx) => (
              <div key={faq.id} className="bg-white rounded-xl border border-blue-100 shadow-sm overflow-hidden">
                <button
                  className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-blue-50/50 transition"
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                >
                  <div className="flex items-center gap-3">
                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">{idx + 1}</span>
                    <span className="font-semibold text-blue-900 text-sm">{faq.question}</span>
                  </div>
                  <svg className={`w-5 h-5 text-blue-400 transition-transform flex-shrink-0 ${openFaq === idx ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" /></svg>
                </button>
                {openFaq === idx && (
                  <div className="px-5 pb-4 pt-0 border-t border-blue-50">
                    <div className="pl-10 text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{faq.answer}</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Videos Tab */}
        {activeTab === 'videos' && (
          <div>
            {!user ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-blue-100 shadow-sm">
                <svg className="w-16 h-16 text-orange-300 mx-auto mb-4" fill="none" stroke="currentColor" strokeWidth="1" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                </svg>
                <p className="text-blue-900 font-bold text-lg mb-2">Videoları izlemek için giriş yapın</p>
                <p className="text-blue-500 text-sm mb-6">Kullanım videolarını izleyebilmek için üye girişi yapmanız gerekmektedir.</p>
                <button
                  onClick={() => navigate('/teklif-olustur')}
                  className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition shadow-md"
                >
                  Giriş Yap
                </button>
              </div>
            ) : (
              <>
                {videos.length === 0 && !loading && (
                  <div className="text-center py-16 bg-white rounded-2xl border border-blue-100 shadow-sm">
                    <svg className="w-16 h-16 text-blue-200 mx-auto mb-4" fill="none" stroke="currentColor" strokeWidth="1" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
                    </svg>
                    <p className="text-blue-900 font-semibold text-lg">Henüz video eklenmemiş</p>
                    <p className="text-blue-500 text-sm mt-1">Yakında kullanım videoları burada yer alacak.</p>
                  </div>
                )}

                {categories.map(cat => {
                  const catVideos = videos.filter(v => (v.category || 'genel') === cat);
                  if (catVideos.length === 0) return null;
                  const catLabels = { genel: 'Genel', 'panel-kullanim': 'Panel Kullanımı', 'siparis': 'Sipariş İşlemleri', 'urun': 'Ürün Yönetimi' };
                  return (
                    <div key={cat} className="mb-8">
                      <h3 className="text-lg font-bold text-blue-900 mb-3 flex items-center gap-2">
                        <span className="w-1.5 h-5 bg-blue-600 rounded-full"></span>
                        {catLabels[cat] || cat}
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {catVideos.map(video => (
                          <div key={video.id} className="bg-white rounded-xl border border-blue-100 shadow-sm overflow-hidden hover:shadow-md transition">
                            {playingVideo === video.id ? (
                              <div className="relative bg-black aspect-video">
                                <video
                                  src={video.video_url}
                                  controls
                                  autoPlay
                                  className="w-full h-full"
                                  onEnded={() => setPlayingVideo(null)}
                                />
                                <button
                                  onClick={() => setPlayingVideo(null)}
                                  className="absolute top-2 right-2 p-1 bg-black/50 rounded-full text-white hover:bg-black/70 transition"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setPlayingVideo(video.id)}
                                className="relative w-full aspect-video bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center group"
                              >
                                {video.thumbnail_url ? (
                                  <img src={video.thumbnail_url} alt={video.title} className="w-full h-full object-cover" />
                                ) : (
                                  <div className="flex flex-col items-center">
                                    <div className="w-16 h-16 bg-blue-600/80 rounded-full flex items-center justify-center group-hover:bg-blue-700 group-hover:scale-110 transition-all shadow-lg">
                                      <svg className="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                                    </div>
                                    <span className="text-xs text-blue-600 font-medium mt-2">Videoyu Oynat</span>
                                  </div>
                                )}
                                {video.thumbnail_url && (
                                  <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                                    <div className="w-14 h-14 bg-blue-600/90 rounded-full flex items-center justify-center shadow-lg">
                                      <svg className="w-7 h-7 text-white ml-1" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                                    </div>
                                  </div>
                                )}
                              </button>
                            )}
                            <div className="p-4">
                              <h4 className="font-bold text-blue-900 text-sm">{video.title}</h4>
                              {video.description && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{video.description}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default HelpPage;
